"use client"

import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react"
import Script from "next/script"

interface ConsentContextType {
  hasConsent: boolean
}

const ConsentContext = createContext<ConsentContextType>({ hasConsent: false })

export function useConsent() {
  return useContext(ConsentContext)
}

interface ConsentProviderProps {
  oneTrustId?: string
  isDevelopment?: boolean
  bypassConsentCheck?: boolean
  children: React.ReactNode
}

const NINETAILED_SDK_POLL_MS = 100
const NINETAILED_SDK_MAX_WAIT_MS = 10_000
// The Ninetailed Privacy Plugin's storage key + accepted-value, written
// via instance.storage.setItem(CONSENT, 'accepted'). Source:
// node_modules/@ninetailed/experience.js-plugin-privacy/src/NinetailedPrivacyPlugin.js
//
// IMPORTANT: analytics.js's storage util JSON.stringify's the value
// before writing, so the raw localStorage value is `"accepted"` (with
// quotes), NOT `accepted`. Reading it back via the analytics.js storage
// API JSON.parses it and returns the unquoted string, which is what
// the privacy plugin's isConsentGiven() compares against. When we read
// localStorage directly we have to handle both forms.
const NT_CONSENT_KEY = "__nt-consent__"
const NT_CONSENT_ACCEPTED_VALUE = "accepted"
const NT_CONSENT_ACCEPTED_VALUE_JSON = '"accepted"'
const NT_CONSENT_WRITE_POLL_MS = 10
const NT_CONSENT_WRITE_MAX_WAIT_MS = 1_000
// Stagger ladder for ninetailed.page() retries after consent is
// granted. The privacy plugin's setItemEnd listener queues a
// SET_ENABLED_FEATURES dispatch through analytics.js's middleware
// chain — multiple chained Promises that don't always drain inside a
// single setTimeout(0) tick under React 18 concurrent rendering. If
// page() fires before that dispatch settles, upsertProfile is called
// with enabledFeatures=[] and the API returns no audiences, so
// <Experience> components stay on baseline. Firing page() at multiple
// staggered intervals gives at least one call a chance to run after
// the dispatch completes. SDK-side asyncThrottle on _flush dedupes
// the API calls so the extra invocations are cheap when the profile
// is already fresh.
const NT_PAGE_RETRY_DELAYS_MS = [0, 500, 1_500, 3_500]
// OneTrust's "Accept all cookies" button id. Same id used by the gatsby
// implementation in trimble-mxp-gatsby/src/utils/hooks/useCheckOneTrust.js.
const ONETRUST_ACCEPT_BTN_ID = "onetrust-accept-btn-handler"
// OneTrust's "Reject all" button id.
const ONETRUST_REJECT_BTN_ID = "onetrust-reject-all-handler"
// OneTrust targeting-cookies category that gates Ninetailed
// personalization. Keep in sync with useCheckOneTrust.js's
// NINETAILED_CONSENT_CATEGORY_ID.
const ONETRUST_TARGETING_CATEGORY_ID = "C0004"

function readNtConsent(): string | null {
  try {
    return window.localStorage.getItem(NT_CONSENT_KEY)
  } catch {
    return null
  }
}

function isNtConsentAccepted(value: string | null): boolean {
  // Match both the raw "accepted" form and the JSON.stringify'd
  // '"accepted"' form that analytics.js's storage util actually writes.
  return (
    value === NT_CONSENT_ACCEPTED_VALUE ||
    value === NT_CONSENT_ACCEPTED_VALUE_JSON
  )
}

// Reads the OneTrust consent decision from the OptanonConsent cookie and
// reports whether the targeting category was accepted. We read the cookie
// rather than window.OnetrustActiveGroups because this needs to be reliable
// on first paint for returning visitors — the cookie is present immediately,
// whereas OnetrustActiveGroups isn't populated until the OneTrust script
// (loaded afterInteractive) finishes initializing. The cookie's `groups`
// param looks like "C0001:1,C0002:1,C0004:0" where :1 means accepted.
function hasOneTrustTargetingConsent(): boolean {
  if (typeof document === "undefined") return false
  const match = document.cookie.match(/(?:^|;\s*)OptanonConsent=([^;]+)/)
  if (!match) return false
  // URLSearchParams percent-decodes values (%3A -> :, %2C -> ,) for us.
  const groups = new URLSearchParams(match[1]).get("groups") || ""
  const normalized = ONETRUST_TARGETING_CATEGORY_ID.replace(/^C0*/, "")
  const candidates = new Set([
    ONETRUST_TARGETING_CATEGORY_ID,
    `C${normalized.padStart(4, "0")}`,
  ])
  return groups.split(",").some(entry => {
    const [id, state] = entry.split(":")
    return state?.trim() === "1" && candidates.has(id?.trim())
  })
}

// Block until __nt-consent__ lands in localStorage (or we give up after
// NT_CONSENT_WRITE_MAX_WAIT_MS). This is what makes the subsequent
// page() call survive the privacy plugin's pageStart hook — pageStart
// calls getConfig() which calls isConsentGiven() which reads
// localStorage via analytics.js's storage util. Until the write lands
// the page event is silently aborted.
async function waitForNtConsentWrite(): Promise<boolean> {
  if (isNtConsentAccepted(readNtConsent())) return true
  const start = Date.now()
  while (Date.now() - start < NT_CONSENT_WRITE_MAX_WAIT_MS) {
    await new Promise<void>(resolve =>
      setTimeout(resolve, NT_CONSENT_WRITE_POLL_MS),
    )
    if (isNtConsentAccepted(readNtConsent())) return true
  }
  return false
}

// Push consent into the Ninetailed SDK and fire a page event so the
// privacy plugin flips into permissive mode and a fresh profile (with
// audiences) is fetched. Returns true if the call ran, false if the SDK
// isn't ready yet so the caller can retry.
//
// IMPORTANT: this needs to run on every page load for any visitor whose
// OneTrust cookie is already set, NOT only on the click of the accept
// button. The Ninetailed privacy plugin keeps its own consent flag in
// analytics.js's in-memory store (globalThis.__global__), which does
// NOT survive a page reload. Without this sync, returning visitors begin
// each session in the restrictive `allowedEvents: []` config wired into
// `NinetailedPrivacyPlugin` (see LayoutComponent), every page() event
// is silently aborted by the plugin's pageStart hook, no profile is
// fetched, audiences never resolve, and every <Experience> sticks on
// its baseline variant — i.e. the personalized variant stays hidden.
//
// Mirrors the gatsby implementation in
// trimble-mxp-gatsby/src/utils/hooks/useCheckOneTrust.js so behavior
// stays consistent between the two sites.
async function syncNinetailedConsent(
  grantConsent = false,
): Promise<boolean> {
  if (typeof window === "undefined") return false
  const ninetailed = (window as any).ninetailed
  if (typeof ninetailed?.consent !== "function") return false

  // OneTrust paths rely on the accept-click handler (or a prior session's
  // persisted __nt-consent__) to grant consent. When OneTrust is disabled
  // or bypassed nothing else ever calls consent(true), so do it here.
  if (grantConsent && !isNtConsentAccepted(readNtConsent())) {
    ninetailed.consent(true)
  }

  // consent(true) dispatches setItemStart through analytics.js, which
  // travels through the middleware chain (multiple chained Promises)
  // before the storage write lands and setItemEnd fires. A single
  // microtask yield (Promise.resolve()) is NOT enough — page() called
  // too soon is silently aborted by the privacy plugin's pageStart hook
  // because getConfig() still returns the pre-consent config. Wait for
  // __nt-consent__ to actually appear in localStorage so the next page()
  // call refreshes the personalization profile (and therefore the
  // <Experience> variants) instead of being dropped on the floor.
  await waitForNtConsentWrite()

  // Fire page() at multiple staggered intervals. The privacy plugin's
  // setItemEnd listener still has to dispatch SET_ENABLED_FEATURES
  // through analytics.js so the core plugin's `enabledFeatures` is the
  // full set when upsertProfile runs. If we fire only one page() call
  // and it loses that race, the API call goes out with
  // enabledFeatures=[] and returns no audiences — <Experience> stays
  // on baseline until the next refresh. Multiple staggered calls let
  // at least one run after the dispatch settles. SDK-side asyncThrottle
  // on _flush dedupes the underlying API calls so this is cheap.
  for (const delay of NT_PAGE_RETRY_DELAYS_MS) {
    if (delay > 0) {
      await new Promise<void>(resolve => setTimeout(resolve, delay))
    }
    try {
      await ninetailed.page?.()
    } catch (error) {
      console.warn("[Ninetailed] page() after consent failed:", error)
    }
  }

  return true
}

/**
 * Loads the OneTrust SDK and gates downstream tracking scripts behind consent.
 * When OneTrust is not enabled (development env or missing ID), consent is
 * granted immediately so scripts load freely.
 */
export function ConsentProvider({
  oneTrustId,
  isDevelopment,
  bypassConsentCheck,
  children,
}: ConsentProviderProps) {
  const isOneTrustEnabled =
    !isDevelopment && !!oneTrustId && !bypassConsentCheck
  const [hasConsent, setHasConsent] = useState(!isOneTrustEnabled)
  // Per-page-load dedupe: stops the cookie-detection effect and the
  // OptanonWrapper click effect from both calling consent(true)+page()
  // (which would double-fire and double-bill page events on visitors
  // who arrive with the OneTrust cookie still on document).
  const ninetailedSyncedRef = useRef(false)

  // Polls until window.ninetailed.consent is attached by the privacy
  // plugin (which initializes asynchronously, racing first paint), then
  // runs the consent + tick + page() handshake exactly once per page
  // load. Caps at NINETAILED_SDK_MAX_WAIT_MS so a misconfigured SDK
  // can't keep timers alive forever.
  const startNinetailedSync = (grantConsent = false): (() => void) => {
    if (typeof window === "undefined") return () => {}
    if (ninetailedSyncedRef.current) return () => {}

    let cancelled = false
    let pollTimer: ReturnType<typeof setTimeout> | null = null

    const trySync = async () => {
      if (cancelled || ninetailedSyncedRef.current) return
      if (await syncNinetailedConsent(grantConsent)) {
        ninetailedSyncedRef.current = true
        return
      }
      pollTimer = setTimeout(trySync, NINETAILED_SDK_POLL_MS)
    }

    trySync()
    const safetyTimeout = setTimeout(() => {
      cancelled = true
      if (pollTimer) clearTimeout(pollTimer)
    }, NINETAILED_SDK_MAX_WAIT_MS)

    return () => {
      cancelled = true
      if (pollTimer) clearTimeout(pollTimer)
      clearTimeout(safetyTimeout)
    }
  }

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (!isOneTrustEnabled) return

    // Returning visitor who already made a choice on a prior session.
    // Only flip the SDK into permissive mode (and render personalized
    // variants without another click) if they actually granted targeting
    // consent — a prior "reject all" must NOT auto-grant consent here.
    if (
      document.cookie.includes("OptanonAlertBoxClosed") &&
      hasOneTrustTargetingConsent()
    ) {
      setHasConsent(true)
      return startNinetailedSync()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOneTrustEnabled])
  /* eslint-enable react-hooks/set-state-in-effect */

  useEffect(() => {
    if (!isOneTrustEnabled) return

    const prev = (window as any).OptanonWrapper
    let cleanup: (() => void) | null = null
    ;(window as any).OptanonWrapper = function () {
      prev?.()
      // OptanonWrapper fires whenever the banner is resolved — accept,
      // reject, or a custom save — and OptanonAlertBoxClosed is set in
      // all of those cases. Gate on the actual targeting decision so a
      // reject doesn't grant consent or kick off the Ninetailed sync.
      if (!document.cookie.includes("OptanonAlertBoxClosed")) return
      if (hasOneTrustTargetingConsent()) {
        setHasConsent(true)
        // syncNinetailedConsent waits for SET_ENABLED_FEATURES to
        // propagate before firing page(), so the audiences come back
        // populated and <Experience> swaps to its variant in place.
        cleanup?.()
        cleanup = startNinetailedSync()
      } else {
        setHasConsent(false)
        // Cancel any sync started by a prior accept on the same page load.
        cleanup?.()
        cleanup = null
      }
    }

    return () => {
      ;(window as any).OptanonWrapper = prev
      cleanup?.()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOneTrustEnabled])

  // Direct click listener on OneTrust's "Accept all cookies" button.
  // Kicks off the syncNinetailedConsent handshake immediately on click
  // so the personalization profile starts refetching the moment the
  // user accepts — slightly ahead of OptanonWrapper firing — and the
  // <Experience> components swap to their variants in place.
  //
  // NOTE: register in the capture phase. OneTrust's accept button
  // handler calls event.stopPropagation() (and removes the banner from
  // the DOM during the same task), which prevents bubble-phase listeners
  // on `document` from ever seeing the click. Capture-phase fires from
  // window → document → … → target *before* any handler on the button
  // can stop propagation, so we always get the click here.
  //
  // Also uses event delegation because the OneTrust banner is injected
  // asynchronously by next/script — the button doesn't exist yet when
  // this effect runs, so we listen on document and filter via
  // event.target.closest('#onetrust-accept-btn-handler').
  useEffect(() => {
    if (!isOneTrustEnabled) return
    if (typeof document === "undefined") return

    let cleanup: (() => void) | null = null
    const handleAcceptAllClick = (event: MouseEvent) => {
      const target = event.target as Element | null
      const acceptBtn = target?.closest?.(`#${ONETRUST_ACCEPT_BTN_ID}`)
      if (!acceptBtn) return
      if (ninetailedSyncedRef.current) return
      (window as any)?.ninetailed?.consent(true)
      setHasConsent(true)
      cleanup?.()
      cleanup = startNinetailedSync()
    }

    // Reject must NOT run the Ninetailed sync — that handshake fires
    // page() and drives the SDK into permissive mode. On reject we only
    // mark consent as denied and cancel any in-flight sync from a prior
    // accept on the same page load.
    const handleRejectAllClick = (event: MouseEvent) => {
      const target = event.target as Element | null
      const rejectBtn = target?.closest?.(`#${ONETRUST_REJECT_BTN_ID}`)
      if (!rejectBtn) return
      (window as any)?.ninetailed?.consent(false)
      setHasConsent(false)
      cleanup?.()
      cleanup = null
    }

    document.addEventListener("click", handleRejectAllClick, true)
    document.addEventListener("click", handleAcceptAllClick, true)
    return () => {
      document.removeEventListener("click", handleAcceptAllClick, true)
      document.removeEventListener("click", handleRejectAllClick, true)
      cleanup?.()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOneTrustEnabled])

  // No-OneTrust path: when BYPASS_CONSENT_CHECK is set in the env profile,
  // NEXT_PUBLIC_BYPASS_CONSENT_CHECK is set at build time, the OneTrust ID
  // is blank, or this is a development deploy, OneTrust is skipped entirely
  // and hasConsent starts as true. We still need to drive the same
  // Ninetailed handshake the accept-button path runs — call consent(true)
  // so __nt-consent__ is written, flip the privacy plugin into permissive
  // mode, and fire the staggered page() retries — otherwise the SDK stays
  // in restrictive `allowedEvents: []` mode: <Experience> components stay
  // on baseline and Insights component events are dropped.
  useEffect(() => {
    if (isOneTrustEnabled) return
    return startNinetailedSync(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOneTrustEnabled])

  return (
    <ConsentContext.Provider value={{ hasConsent }}>
      {isOneTrustEnabled && (
        <Script
          id="onetrust-sdk"
          src="https://cdn.cookielaw.org/scripttemplates/otSDKStub.js"
          strategy="afterInteractive"
          data-domain-script={oneTrustId}
        />
      )}
      {children}
    </ConsentContext.Provider>
  )
}
